<template>
  <div :id="'message-' + message.id"
       class="chat-bubble-row"
       :class="{
         'chat-bubble-row--sent': isSent,
         'chat-bubble-row--continuation': !firstOfRun,
       }">
    <!-- Avatar column only for received messages in group conversations. Own
         messages and 1:1 conversations render no avatar; within a group the
         avatar shows on the first message of a consecutive same-author run and
         continuation bubbles indent-align under an empty slot. -->
    <div v-if="showAvatarColumn" class="chat-bubble-avatar-slot">
      <div v-if="showAvatarImage" class="chat-bubble-avatar" :style="{ backgroundColor: avatarColor }">
        <img v-if="avatarUrl"
             :src="avatarUrl"
             :alt="message.author.display_name"
             class="chat-bubble-avatar__img"
             @error="avatarError = true" />
        <template v-else>{{ avatarInitials }}</template>
      </div>
    </div>
    <div class="chat-bubble-col">
      <div v-if="showMeta" class="chat-bubble-meta">
        <a v-if="showName" class="chat-bubble-meta__author" data-cy="message-author" @click.prevent="findDirectMessage()">
          {{ message.author.display_name }}<spinner :inline="true" :ml="5" v-if="findingMessage"></spinner>
        </a>
        <span v-if="firstOfRun" class="chat-bubble-meta__time">{{ formattedTime }}</span>
      </div>

      <template v-if="message.poll">
        <poll-card
          :poll="message.poll"
          :message-id="message.id"
          :active-profile-id="activeProfileId"
          :active-profile-type="activeProfileType"
          :is-coach="isCoach"
          :message-author-id="message.author.id"
          :message-author-type="message.author.author_type"
        ></poll-card>
      </template>
      <template v-else>
        <div class="chat-bubble"
             data-cy="message-bubble"
             :class="[
               isSent ? 'chat-bubble--sent' : 'chat-bubble--received',
               { 'chat-bubble--highlighted': highlighted }
             ]">
          <div class="trix-content" v-html="content"></div>
          <message-attachments :attachments="message.attachments"></message-attachments>
          <button v-if="canEdit"
                  type="button"
                  class="chat-bubble__edit-btn"
                  data-cy="message-edit-btn"
                  title="Edit message"
                  aria-label="Edit message"
                  @click="$emit('edit-message', message)">
            <i class="fa-solid fa-pencil" aria-hidden="true"></i>
          </button>
        </div>
      </template>

      <div v-if="editCount > 0" class="chat-bubble-edited-row">
        <button type="button"
                class="chat-bubble-edited"
                data-cy="message-edited"
                :title="`View edit history (${editCount} ${editCount === 1 ? 'edit' : 'edits'})`"
                @click="showVersions = true">
          (edited · v{{ editCount + 1 }})
        </button>
      </div>

      <div v-if="reactionsEnabled" class="chat-bubble-reactions">
        <message-reactions
          :message-id="message.id"
          :reaction-summary="message.reaction_summary || {}"
          :current-user-reactions="message.current_user_reactions || []"
        ></message-reactions>
      </div>
    </div>

    <message-versions-modal
      v-if="showVersions"
      :message="message"
      :message-group-id="message.message_group_id"
      @close="showVersions = false"
    ></message-versions-modal>
  </div>
</template>

<script>
import qs from 'qs';
import Spinner from './spinner.vue';
import MessageAttachments from './message_attachments.vue';
import MessageReactions from './message_reactions.vue';
import PollCard from './poll_card.vue';
import MessageVersionsModal from './message_versions_modal.vue';
import { urlHelperMixin } from './mixins/url_helper_mix';
import { avatarInitials, avatarColor } from '../utilities/avatars';

export default {
  name: 'message',
  components: {
    MessageAttachments, Spinner, MessageReactions, PollCard, MessageVersionsModal,
  },
  mixins: [urlHelperMixin],
  props: {
    message: {
      type: Object,
      required: true,
    },
    activeProfileId: {
      type: Number,
      required: true,
    },
    activeProfileType: {
      type: String,
      required: true,
    },
    reactionsEnabled: {
      type: Boolean,
      default: false,
    },
    highlighted: {
      type: Boolean,
      default: false,
    },
    isCoach: {
      type: Boolean,
      default: false,
    },
    // True when this message is in a 2-person conversation (no per-message avatars).
    isPair: {
      type: Boolean,
      default: false,
    },
    // True when this is the first message of a consecutive same-author run.
    // Only the first of a run shows the avatar + name + time.
    firstOfRun: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      findingMessage: false,
      showVersions: false,
      avatarError: false,
    };
  },
  computed: {
    content() {
      if (this.hasRichContent) return this.message.rich_content;
      if (!this.message.content) return '';
      return this.plainContentHtml(this.message.content);
    },
    hasRichContent() {
      return this.message.rich_content && this.message.rich_content.length > 0;
    },
    isSent() {
      return this.message.author.id === this.activeProfileId
        && this.message.author.author_type === this.activeProfileType;
    },
    editCount() {
      return this.message.edit_count || 0;
    },
    canEdit() {
      if (this.message.poll) return false;
      return this.isSent || this.isCoach;
    },
    // Received messages in a group reserve a fixed avatar column so continuation
    // bubbles indent-align under the run; own messages and 1:1 chats render none.
    showAvatarColumn() {
      return !this.isSent && !this.isPair;
    },
    // Avatar shows on the first message of a run only.
    showAvatarImage() {
      return this.showAvatarColumn && this.firstOfRun;
    },
    // The meta row (name/time) shows on the first of a run only; the edited
    // marker renders below the bubble so it doesn't need a meta row.
    showMeta() {
      return this.firstOfRun;
    },
    // Name only for received messages in a group (own messages and 1:1s don't
    // need it — it's you, or it's in the header).
    showName() {
      return this.firstOfRun && !this.isSent && !this.isPair;
    },
    avatarUrl() {
      // Fall back to initials if the author has no photo or the image fails to load.
      if (this.avatarError) return null;
      return this.message.author.avatar_url || null;
    },
    avatarInitials() {
      return avatarInitials(this.message.author.display_name);
    },
    avatarColor() {
      return avatarColor(this.message.author.id);
    },
    formattedTime() {
      // Show when it was sent, not when it was drafted (a scheduled message is
      // created earlier than it sends). Fall back to created_at for legacy rows.
      const timestamp = this.message.sent_at || _.get(this.message, 'created_at', null);
      if (timestamp === null) return '';
      return moment(timestamp).format('h:mma');
    },
  },
  methods: {
    findDirectMessage() {
      const vm = this;
      if (vm.findingMessage) return;

      vm.findingMessage = true;
      const profiles = [
        { id: vm.message.author.id, type: vm.message.author.author_type },
        { id: vm.activeProfileId, type: vm.activeProfileType },
      ];
      const params = { profiles, type: 'direct' };

      const url = vm.$apiService.messageGroupsUrl();
      axios.get(url, {
        params,
        paramsSerializer(p) {
          return qs.stringify(p, { arrayFormat: 'brackets' });
        },
      })
        .then((response) => {
          if (response.data.message_groups.length > 0) {
            vm.findingMessage = false;
            const groupId = response.data.message_groups[0].id;
            window.location.assign(`/message_groups/${groupId}`);
            return;
          }
          vm.createDirectMessage();
        })
        .catch(() => {
          // Falls through to allow creation
        });
    },
    createDirectMessage() {
      const vm = this;
      const url = vm.$apiService.messageGroupsUrl();

      const memberships = [
        {
          profile_id: vm.message.author.id,
          profile_type: vm.message.author.author_type,
          can_publish: true,
        },
        {
          profile_id: vm.activeProfileId,
          profile_type: vm.activeProfileType,
          can_publish: true,
        },
      ];
      const params = {
        message_group: {
          name: null,
          group_type: 'direct',
          purpose: null,
          default_allow_publish: 'all',
          message_memberships_attributes: memberships,
        },
      };

      axios.post(url, params)
        .then((response) => {
          vm.findingMessage = false;
          window.location.assign(`/message_groups/${response.data.id}`);
        })
        .catch((error) => {
          vm.error = true;
          vm.errorMessage = `Error creating message group ${error.toString()}`;
          vm.findingMessage = false;
        });
    },
  },
};
</script>
